import { apiClient } from "model/axiosConfig";
import { typeProductsFilters } from "./dProductsReducer";
import { typeRequestPagination } from "../system/constants";
import { changeNullValueWithAlias } from "constants/nullAlias";
import { typeAddProductsItemObject } from "components/containers/dashboard/dataProducts/popupContents/popupAdd";
import { typeEditProductsItemObject } from "components/containers/dashboard/dataProducts/popupContents/popupEdit";
import { typeBulkChangeCategoryObject } from "components/containers/dashboard/dataProducts/popupContents/popupBulkChangeCategory";

// Type
export type typeProductRequestFilters = {
  filters: {
    psid?: string | null;
    name?: string | string[] | null;
    category?: {
      id: string;
    } | null;
    integerUnit?: boolean | null;
    active?: boolean | null;
    marked?: boolean | null;
    deleted?: boolean | null;
    barcodes?: {
      in: string[];
    } | null;
    stores?: {
      store: {
        id: string[];
      };
    } | null;
  };
  pageFilter: typeRequestPagination;
  sortFilter?: {
    direction: string;
    column: string;
  };
};

// Get product list
export const getProductList = async (payload: {
  filters: typeProductsFilters;
  paginationRequest?: typeRequestPagination;
}) => {
  const path = "/product/product/v2/search";
  const config = {
    headers: {},
  };
  const data: typeProductRequestFilters = {
    filters: {},
    pageFilter: payload.paginationRequest!,
    sortFilter: {
      direction: "ASC",
      column: "name",
    },
  };

  if (payload.filters.name) {
    data.filters.name! = [payload.filters.name]
  }

  if ("integerUnit" in payload.filters) {
    data.filters.integerUnit = payload.filters.integerUnit;
  }

  if ("marked" in payload.filters) {
    data.filters.marked = payload.filters.marked;
  }

  if ("deleted" in payload.filters) {
    data.filters.deleted = payload.filters.deleted;
  }

  if ("invalidPsid" in payload.filters) {
    data.filters.psid = payload.filters.invalidPsid === true ? "invalid" : null;
  }

  if (payload.filters.category !== undefined) {
    if (payload.filters.category === 'outOfCategories') {
      data.filters.category = null;
    } else if (payload.filters.category) {
      data.filters.category = { id: payload.filters.category };
    } else {
      data.filters.category = undefined;
    }
  }

  if (payload.filters.barcode) {
    data.filters.barcodes = { in: [payload.filters.barcode] };
  }

  if (payload.filters.store) {
    data.filters.stores = { store: { id: [payload.filters.store] } };
  }

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// search product by name
export const searchProductByName = async (payload: {
  filters: { name: string };
}) => {
  const path = "/product/product/search";
  const config = {
    headers: {},
  };
  const data = {
    filters: payload.filters,

    sortFilter: {
      direction: "ASC",
      column: "name",
    },
  };

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Get product item by id
export const getProductItemById = async (payload: { itemId: string }) => {
  const path = `/product/product/exists/id/?id=${payload.itemId}`;
  const config = {
    headers: {},
  };

  try {
    return await apiClient.get(path, config);
  } catch (error) {
    return error.response;
  }
};

// Search product item by barcode
export const getProductItemByBarcode = async (payload: { barcode: string }) => {
  const path = `/product/product/v2/search`;
  const config = {
    headers: {},
  };
  const data = {
    filters: {
      barcodes: {
        in: [payload.barcode],
      },
    },
  };
  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    return error.response;
  }
};

// Set product item
export const changeProductItem = async (payload: {
  itemObject: typeEditProductsItemObject;
}) => {
  const path = "/product/product";
  const config = {
    headers: {},
  };
  const data = changeNullValueWithAlias(payload.itemObject);

  try {
    return await apiClient.patch(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Add product item
export const addProductItem = async (payload: {
  itemObject: typeAddProductsItemObject;
}) => {
  const path = "/product/product";
  const config = {
    headers: {},
  };
  const data = payload.itemObject;

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Delete product item
export const deleteProductItem = async (payload: { itemId: string }) => {
  const path = "/product/product/delete";
  const config = {
    headers: {},
  };
  const data = [payload.itemId];
  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Bulk change items
export const bulkChangeItems = async (payload: {
  bulkObjectList: typeBulkChangeCategoryObject[];
}) => {
  const path = "/product/product/bulk";
  const config = {
    headers: {},
  };
  const data = payload;

  try {
    return await apiClient.patch(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Bulk delete items
export const bulkDeleteProducts = async (payload: { bulkIdList: string[] }) => {
  const path = "/product/product/delete";
  const config = {
    headers: {},
  };
  const data = payload.bulkIdList;

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

export const deleteProductsFromStore = async (payload: {
  storeId: string | number;
}) => {
  const path = "/product/product/delete/" + payload.storeId;

  try {
    return await apiClient.post(path);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Bulk recover items
export const bulkRecoverProducts = async (payload: {
  bulkIdList: string[];
}) => {
  const path = "/product/product/restore";
  const config = {
    headers: {},
  };
  const data = payload.bulkIdList;

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Bulk off sale
export const bulkOffSaleProducts = async (payload: {
  productIds: string[];
  storeIds: string[];
}) => {
  const path = "/product/product/retail/withdraw";
  const config = {
    headers: {},
  };
  const data = {
    productIds: payload.productIds,
    storeIds: payload.storeIds,
  };

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Bulk delete from store
export const bulkDeleteFromStoreProducts = async (payload: {
  productIds: string[];
  storeIds: string[];
}) => {
  const path = "/product/product/retail/remove";
  const config = {
    headers: {},
  };
  const data = {
    productIds: payload.productIds,
    storeIds: payload.storeIds,
  };

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// Export data
export const exportDataProducts = async (payload: {
  productIds: string[];
  storeIds: string[];
  lang: string;
}) => {
  const path = `/product/product/export?lang=${payload.lang}`;
  const config = {
    headers: {},
    responseType: "blob",
  };
  const data = payload;

  try {
    // @ts-ignore
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// import data
export const importDataProducts = async (payload: { formData: FormData }) => {
  const path = "/product/product/import";
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const data = payload.formData;

  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

export const saveTemplateProductsFile = async (lang: string) => {
  const path = `/product/product/export?lang=${lang}&template=true`;
  const config: { headers: {}; responseType: "blob" } = {
    headers: {},
    responseType: "blob",
  };
  const data = {
    productIds: [],
    storeIds: [],
  };
  try {
    return await apiClient.post(path, data, config);
  } catch (error) {
    console.log(error);
    return error;
  }
};

// check product  PSID on exist in base
export const checkExistPSID = async (payload: { psid: string }) => {
  const path = `/product/product/catalog/exists/psid?psid=${payload.psid}`;
  const config = {
    headers: {},
  };

  try {
    return await apiClient.get(path, config);
  } catch (error) {
    return error.response;
  }
};

// restore with new data product item
export const restoreWithNewDataProductItem = async (payload: {
  itemObject: typeEditProductsItemObject;
}) => {
  const path = "/product/product";
  const config = {
    headers: {},
  };

  const data = changeNullValueWithAlias(payload.itemObject);

  try {
    return await apiClient.patch(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// change vat for all products
export const changeVatForAllProducts = async (payload: { vat: number }) => {
  const path = "/product/product/patch/vat/bulk";
  const config = {
    headers: {},
  };
  const data = payload;

  try {
    return await apiClient.patch(path, data, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

// delete all products
export const deleteAllProducts = async () => {
  const path = "/product/product/delete/all";
  const config = {
    headers: {},
  };

  try {
    return await apiClient.post(path, {}, config);
  } catch (error) {
    console.log(error);
    return error.response;
  }
};

export const getProductByPsid = async (psid: string) => {
  try {
    return await apiClient.get(`/product/product/catalog/${psid}`);
  } catch (error) {
    return error.response;
  }
};

export const getProductByBarcode = async (barcode: string) => {
  try {
    return await apiClient.get(`/product/product/catalog/barcode/${barcode}`);
  } catch (error) {
    return error.response;
  }
};
